<template>
  <v-col cols="12" md="6">
    <card class="h-100">
      <div class="text-h6 mb-2">Automaatne varundus</div>
      <p class="text-grey-darken-1">
        Teeb igal ööl valitud ajal krüpteeritud varukoopia serverisse. Sealt laeb Synology selle alla.
        Serveris hoitakse ainult viimased koopiad.
      </p>
      <template v-if="auto">
        <v-alert v-if="auto.hasPassword" type="success" variant="tonal" density="compact" class="mb-3">
          Krüpteering: BACKUP_PASSWORD on seadistatud (AES-256).
          <div class="mt-1">Hoia parool ka paroolihalduris — ilma selleta varukoopiat avada ei saa.</div>
        </v-alert>
        <v-alert v-else type="error" variant="tonal" density="compact" class="mb-3">
          Krüpteerimise parool puudub. Lisa serveri <code>.env</code> faili rida <code>BACKUP_PASSWORD=…</code>
          ja taaskäivita intra. Seni automaatset varundust sisse lülitada ei saa.
        </v-alert>

        <v-switch
          v-model="form.enabled"
          :label="form.enabled ? 'Sees' : 'Väljas'"
          color="primary"
          hide-details
          inset
          :disabled="!auto.hasPassword"
        />
        <v-row dense class="mt-2">
          <v-col cols="12" sm="6">
            <time-field v-model="form.time" label="Kellaaeg" density="compact" />
          </v-col>
        </v-row>
        <v-checkbox
          :model-value="true"
          disabled
          label="Andmebaas — iga öö"
          :hint="'MySQL andmebaasi koopia (database.sql)' + (estimate ? ' · kuni ~' + size(estimate.database.bytes) : '')"
          persistent-hint
          class="mt-0"
        />
        <v-row dense class="mt-2">
          <v-col cols="12" sm="6">
            <v-select
              v-model="form.filesEvery"
              :items="filesEveryItems"
              label="Failid (pildid ja failid)"
              density="compact"
              :hint="estimate ? 'uploads/ · ' + size(estimate.files.bytes) + ', ' + count(estimate.files.count) + ' faili' : ''"
              persistent-hint
            />
          </v-col>
          <v-col v-if="form.filesEvery === 'weekly'" cols="12" sm="6">
            <v-select v-model="form.filesDay" :items="weekdayItems" label="Nädalapäev" density="compact" />
          </v-col>
        </v-row>
        <p class="text-body-2 text-grey-darken-1 mt-2">{{ filesExplain }}</p>
        <v-row dense class="mt-2">
          <v-col cols="12" sm="6">
            <v-text-field v-model.number="form.keepDb" type="number" min="1" max="30" label="Säilita serveris andmebaasi koopiaid" density="compact" />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model.number="form.keepFull"
              type="number"
              min="1"
              max="30"
              label="Säilita serveris täiskoopiaid"
              density="compact"
              :disabled="form.filesEvery === 'never'"
            />
          </v-col>
        </v-row>
        <v-text-field
          v-model="form.alertEmail"
          type="email"
          label="Hoiatus e-postile, kui varundus ebaõnnestub"
          density="compact"
          autocomplete="off"
        />
        <v-btn color="primary" :loading="saving" @click="save">Salvesta</v-btn>
        <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mt-3">{{ error }}</v-alert>

        <v-divider class="my-4" />
        <div class="facts text-body-2">
          <span class="text-grey-darken-1">Viimane</span>
          <span v-if="auto.lastRun">
            <v-chip :color="auto.lastRun.ok ? 'green-darken-2' : 'red-darken-2'" size="x-small" variant="tonal" class="mr-1">
              {{ auto.lastRun.ok ? 'OK' : 'Viga' }}
            </v-chip>
            {{ when(auto.lastRun.at) }}
            <template v-if="auto.lastRun.ok"> · {{ size(auto.lastRun.bytes) }}</template>
            · {{ auto.lastRun.full ? 'täiskoopia' : 'andmebaas' }}{{ auto.lastRun.reason === 'manual' ? ', käsitsi' : '' }}
          </span>
          <span v-else>—</span>
          <span class="text-grey-darken-1">Järgmine</span>
          <span>
            <template v-if="auto.nextRun">{{ when(auto.nextRun.at) }} ({{ auto.nextRun.full ? 'täiskoopia' : 'andmebaas' }})</template>
            <template v-else>— (väljas)</template>
          </span>
        </div>
        <v-alert v-if="auto.lastRun && !auto.lastRun.ok" type="error" variant="tonal" density="compact" class="mt-3">
          {{ when(auto.lastRun.at) }} ebaõnnestus: {{ auto.lastRun.error }}. Eelmised koopiad jäid alles.
        </v-alert>
        <div class="mt-3">
          <v-btn variant="text" color="primary" :disabled="!auto.hasPassword || !!auto.running" @click="runNow(false)">
            Käivita kohe: andmebaas
          </v-btn>
          <v-btn variant="text" color="primary" :disabled="!auto.hasPassword || !!auto.running" @click="runNow(true)">
            Käivita kohe: täiskoopia
          </v-btn>
        </div>
        <div v-if="auto.running" class="text-body-2 text-grey-darken-1 mt-1">
          <v-progress-circular indeterminate size="16" width="2" color="primary" class="mr-2" />
          Koostan ja krüpteerin {{ auto.running.full ? 'täiskoopiat' : 'andmebaasi koopiat' }}…
        </div>

        <div class="text-overline text-grey-darken-1 mt-4">Koopiad serveris</div>
        <div v-if="!auto.copies.length" class="text-body-2 text-grey-darken-1">Koopiaid veel ei ole.</div>
        <div v-else class="copies">
          <v-table density="compact">
            <thead>
              <tr><th>Koopia</th><th>Sisu</th><th class="text-right">Suurus</th><th>Synology</th><th /></tr>
            </thead>
            <tbody>
              <tr v-for="copy in auto.copies" :key="copy.name">
                <td>{{ when(copy.createdAt) }}</td>
                <td>{{ copy.full ? 'Täis' : 'Andmebaas' }}</td>
                <td class="text-right">{{ size(copy.bytes) }}</td>
                <td>
                  <v-chip :color="copy.collected ? 'green-darken-2' : 'grey'" size="x-small" variant="tonal">
                    {{ copy.collected ? 'kätte saadud' : 'ootab' }}
                  </v-chip>
                </td>
                <td><a :href="copyUrl(copy.name)">Lae alla</a></td>
              </tr>
            </tbody>
          </v-table>
        </div>
        <p class="text-caption text-grey-darken-1 mt-2">
          Siit alla laaditud .zip.enc faili oskab Taasta kaart ise lahti teha.
        </p>
      </template>
    </card>
  </v-col>

  <v-col cols="12" md="6">
    <card class="h-100">
      <div class="text-h6 mb-2">Synology</div>
      <p class="text-grey-darken-1">
        Synology käivitab igal ööl skripti, mis küsib intralt uusima koopia ja laeb selle alla.
        Ligipääs käib salajase võtmega; võti näeb ainult varukoopiaid.
      </p>
      <template v-if="auto">
        <div class="facts text-body-2">
          <span class="text-grey-darken-1">Võti</span>
          <span>{{ auto.token.exists ? 'Loodud ' + when(auto.token.createdAt) + ' · kasutusel' : 'Puudub — Synology ei saa koopiaid alla laadida' }}</span>
          <span class="text-grey-darken-1">Viimati kätte saanud</span>
          <span>
            <template v-if="lastPull">{{ when(lastPull.at) }} · {{ lastPull.name }} · {{ lastPull.ip }}</template>
            <template v-else>— (pole veel käinud)</template>
          </span>
        </div>
        <v-alert v-if="pullStale" type="warning" variant="tonal" density="compact" class="mt-3">
          Synology pole uusimat koopiat ({{ when(auto.copies[0].createdAt) }}) üle 24 tunni kätte saanud.
          Kontrolli, kas Synology on sees ja ülesanne töötab.
        </v-alert>

        <v-alert v-if="newToken" type="info" variant="tonal" density="compact" class="mt-3">
          Uus võti. Kopeeri see kohe skripti: hiljem seda enam ei näidata.
          <template v-if="replacedToken">Vana võti lakkas töötamast.</template>
          <div class="token mt-2">{{ newToken }}</div>
          <v-btn size="small" variant="text" color="primary" class="mt-1" @click="copy(newToken, 'Võti kopeeritud')">Kopeeri võti</v-btn>
        </v-alert>

        <div class="mt-3">
          <v-btn color="primary" :loading="tokenBusy" :disabled="!auto.hasPassword" @click="makeToken">
            {{ auto.token.exists ? 'Asenda võti' : 'Loo võti' }}
          </v-btn>
          <v-btn v-if="auto.token.exists && !confirmRevoke" variant="text" color="red-darken-2" class="ml-2" @click="confirmRevoke = true">
            Tühista
          </v-btn>
        </div>
        <v-alert v-if="confirmRevoke" type="error" variant="tonal" density="compact" class="mt-3">
          Tühistamise järel ei saa Synology enam koopiaid alla laadida, kuni lood uue võtme.
          <div class="mt-2">
            <v-btn size="small" color="red-darken-2" :loading="tokenBusy" @click="revokeToken">Jah, tühista</v-btn>
            <v-btn size="small" variant="text" class="ml-2" @click="confirmRevoke = false">Loobu</v-btn>
          </div>
        </v-alert>

        <v-divider class="my-4" />
        <div class="text-overline text-grey-darken-1">Synology skript</div>
        <pre class="script">{{ script }}</pre>
        <v-btn size="small" variant="text" color="primary" class="mt-1" @click="copy(script, newToken ? 'Skript kopeeritud koos võtmega' : 'Skript kopeeritud — lisa sinna oma võti')">
          Kopeeri skript
        </v-btn>

        <div class="text-overline text-grey-darken-1 mt-3">Seadistamine (DSM 7)</div>
        <ol class="text-body-2 steps">
          <li>Loo jagatud kaust, nt <code>varukoopiad/intra</code>, ja muuda skriptis DEST vastavaks.</li>
          <li>Juhtpaneel → Ülesannete ajakava → Loo → Ajastatud ülesanne → Kasutaja määratud skript.</li>
          <li>Üldine: nimi „Intra varukoopia“, kasutaja: kausta omanik (mitte root).</li>
          <li>Ajakava: iga päev kell {{ pullTime }} (tund pärast varundust).</li>
          <li>Ülesande seaded: kleebi skript; lülita sisse „Saada e-postiga, kui skript lõpeb vigaselt“.</li>
          <li>Salvesta, vali ülesanne ja vajuta Käivita. Siia ilmub „Viimati kätte saanud“.</li>
        </ol>

        <div class="text-overline text-grey-darken-1 mt-3">Koopia avamine käsitsi</div>
        <pre class="script">{{ auto.command }} -in KOOPIA.zip.enc -out varukoopia.zip</pre>
        <p class="text-caption text-grey-darken-1 mt-1">
          Küsib parooli (BACKUP_PASSWORD). Töötab Maci, Linuxi ja Synology käsureal; Windowsis Git Bashis.
        </p>
      </template>
    </card>
  </v-col>
</template>

<script>
import api from '@/api'
import { mapActions } from 'vuex'

// Süsteem → Varundus: Automaatne varundus and the Synology that collects its
// copies (server/service/autoBackup.js). Two cards, laid into Backup.vue's row.

const WEEKDAYS = ['esmaspäeva', 'teisipäeva', 'kolmapäeva', 'neljapäeva', 'reede', 'laupäeva', 'pühapäeva']
const pad = n => String(n).padStart(2, '0')

// The script DSM's Task Scheduler runs. The rm target is guarded with :? so an
// empty DEST or name stops the script instead of reaching for /.
const SCRIPT_BODY = [
  'meta=$(curl -fsS -H "Authorization: Bearer $TOKEN" "$URL/latest") || exit 1',
  'name=$(echo "$meta" | sed -n \'s/.*"name":"\\([^"]*\\)".*/\\1/p\')',
  'sum=$(echo "$meta" | sed -n \'s/.*"sha256":"\\([^"]*\\)".*/\\1/p\')',
  '[ -f "$DEST/$name" ] && exit 0',
  'curl -fsS -H "Authorization: Bearer $TOKEN" -o "$DEST/$name.part" "$URL/file/$name" || exit 1',
  'echo "$sum  $DEST/$name.part" | sha256sum -c - >/dev/null || { rm -f "${DEST:?}/${name:?}.part"; exit 1; }',
  'mv "$DEST/$name.part" "$DEST/$name"',
  'find "$DEST" -name \'gaspre-auto-*.zip.enc\' -mtime +$KEEP_DAYS -delete'
]

export default {
  props: {
    estimate: { type: Object, default: null }
  },
  data () {
    return {
      auto: null,
      timer: null,
      form: { enabled: false, time: '03:00', filesEvery: 'weekly', filesDay: 7, keepDb: 3, keepFull: 2, alertEmail: '' },
      saving: false,
      error: null,
      newToken: null,
      replacedToken: false,
      tokenBusy: false,
      confirmRevoke: false,
      filesEveryItems: [
        { text: 'Iga päev', value: 'daily' },
        { text: 'Kord nädalas', value: 'weekly' },
        { text: 'Kord kuus (1. kuupäeval)', value: 'monthly' },
        { text: 'Mitte kunagi', value: 'never' }
      ],
      weekdayItems: ['Esmaspäev', 'Teisipäev', 'Kolmapäev', 'Neljapäev', 'Reede', 'Laupäev', 'Pühapäev']
        .map((text, i) => ({ text, value: i + 1 }))
    }
  },
  computed: {
    filesExplain () {
      const f = this.form
      if (f.filesEvery === 'daily') return 'Iga öö tehakse täiskoopia (andmebaas + failid).'
      if (f.filesEvery === 'never') return 'Failid ei lähe automaatsesse varukoopiasse; neid saab alla laadida käsitsi (Lae alla).'
      const day = WEEKDAYS[f.filesDay - 1] || WEEKDAYS[6]
      const when = f.filesEvery === 'weekly' ? day[0].toUpperCase() + day.slice(1) + ' öösel' : 'Iga kuu 1. kuupäeva öösel'
      return when + ' tehakse täiskoopia (andmebaas + failid), teistel öödel ainult andmebaas. Synology laeb nii vähem alla.'
    },
    lastPull () {
      return this.auto && this.auto.pulls.find(p => p.ok)
    },
    // The newest copy has waited over a day without the Synology taking it.
    pullStale () {
      const newest = this.auto && this.auto.token.exists && this.auto.copies[0]
      return !!newest && !newest.collected && Date.now() - new Date(newest.createdAt).getTime() > 24 * 60 * 60 * 1000
    },
    pullTime () {
      const [h, m] = (this.form.time || '03:00').split(':').map(Number)
      return pad((h + 1) % 24) + ':' + pad(m || 0)
    },
    // The token is filled in only right after it was made; it is never shown
    // again after that.
    script () {
      return [
        '#!/bin/sh',
        'URL="' + new URL('/api/backup-pull', document.baseURI).href + '"',
        'TOKEN="' + (this.newToken || 'SIIA-SINU-VÕTI') + '"',
        'DEST="/volume1/varukoopiad/intra"',
        'KEEP_DAYS=60',
        '',
        ...SCRIPT_BODY
      ].join('\n')
    }
  },
  mounted () {
    this.load(true)
  },
  beforeUnmount () {
    clearTimeout(this.timer)
  },
  methods: {
    ...mapActions(['showNotification']),
    // Fills the form only when asked: a poll while a copy runs must not undo
    // what is being typed.
    load (fillForm) {
      return api.getAutoBackup().then(auto => this.set(auto, fillForm)).catch(() => {})
    },
    set (auto, fillForm) {
      this.auto = auto
      if (fillForm) this.form = { ...auto.settings }
      clearTimeout(this.timer)
      if (auto.running) this.timer = setTimeout(() => this.load(false), 2000)
    },
    async save () {
      this.saving = true
      this.error = null
      try {
        this.set(await api.saveAutoBackup(this.form), true)
        this.showNotification({ text: 'Salvestatud', color: 'success' })
      } catch (err) {
        this.error = (err && err.message) || 'Salvestamine ebaõnnestus'
      } finally {
        this.saving = false
      }
    },
    async runNow (full) {
      this.error = null
      try {
        this.set(await api.runAutoBackup(full), false)
      } catch (err) {
        this.error = (err && err.message) || 'Varundust ei saanud alustada'
      }
    },
    async makeToken () {
      this.tokenBusy = true
      this.error = null
      try {
        const existed = this.auto.token.exists
        const { token, status } = await api.createBackupToken()
        this.newToken = token
        this.replacedToken = existed
        this.set(status, false)
      } catch (err) {
        this.error = (err && err.message) || 'Võtit ei saanud luua'
      } finally {
        this.tokenBusy = false
      }
    },
    async revokeToken () {
      this.tokenBusy = true
      try {
        this.set(await api.revokeBackupToken(), false)
        this.newToken = null
        this.confirmRevoke = false
        this.showNotification({ text: 'Võti tühistatud', color: 'success' })
      } catch (err) {
        this.error = (err && err.message) || 'Võtit ei saanud tühistada'
      } finally {
        this.tokenBusy = false
      }
    },
    copy (text, message) {
      const failed = () => this.showNotification({ text: 'Kopeerimine ei õnnestunud — vali tekst käsitsi', color: 'red' })
      try {
        navigator.clipboard.writeText(text).then(() => this.showNotification({ text: message, color: 'success' }), failed)
      } catch (e) {
        failed()
      }
    },
    copyUrl (name) {
      return api.autoBackupFileUrl(name)
    },
    when (iso) {
      if (!iso) return ''
      const d = new Date(iso)
      return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear() + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes())
    },
    size (bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' kB'
      if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1).replace('.', ',') + ' MB'
      return (bytes / 1024 / 1024 / 1024).toFixed(2).replace('.', ',') + ' GB'
    },
    count (n) {
      return n.toLocaleString('et-EE')
    }
  }
}
</script>

<style scoped>
.facts {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 4px 16px;
  font-variant-numeric: tabular-nums;
}
.facts > span {
  overflow-wrap: anywhere;
}
.copies {
  overflow-x: auto;
}
.copies td {
  white-space: nowrap;
}
.token {
  font-family: ui-monospace, Menlo, Consolas, monospace;
  font-size: 13px;
  word-break: break-all;
  user-select: all;
}
.script {
  background: #263238;
  color: #eceff1;
  font: 12px/1.6 ui-monospace, Menlo, Consolas, monospace;
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  white-space: pre;
  max-width: 100%;
}
.steps {
  padding-left: 20px;
}
.steps li + li {
  margin-top: 2px;
}
</style>
